// VAT on a sale. The organization rate is a percent (15 for 15%) kept in erp_options as `vatRate`
// and used while the `sale.vatRate` flag is on. A saved sale stores it as a fraction on
// `vatAmount` (0.15), with 4 places so 7.5% stays 0.075. Mirrors the API's salesVat.utils.ts.

export const VAT_RATE_OPTION = 'vatRate';

const roundToTwoDecimals = (value: number) => Number(value.toFixed(2));

// The options store keeps strings. Anything unreadable or outside 0 to 100 counts as no VAT.
export const parseVatRatePercent = (value: unknown): number => {
  const percent = typeof value === 'number' ? value : Number(String(value ?? '').trim());
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
    return 0;
  }
  return percent;
};

export const vatPercentToStoredFraction = (percent: number | null | undefined): number =>
  percent ? Number((percent / 100).toFixed(4)) : 0;

export const storedVatFractionToPercent = (storedVatFraction: number | null | undefined): number =>
  storedVatFraction ? Number((storedVatFraction * 100).toFixed(2)) : 0;

// VAT for an amount at a percent, as the API adds it to a new sale.
export const calculateVatAmount = (amount: number, percent: number): number =>
  roundToTwoDecimals(amount * vatPercentToStoredFraction(percent));

// VAT of a saved sale, from the fraction it stores.
export const calculateStoredSaleVatAmount = (
  subTotal: number | null | undefined,
  storedVatFraction: number | null | undefined,
): number => roundToTwoDecimals((subTotal || 0) * (storedVatFraction || 0));

// A VAT or tax line is shown only when there is VAT: a rate above 0 or an amount above 0. At 0, or
// with VAT off, screens and printouts leave the line out.
export const showsVat = (vatRatePercent: number | null | undefined, vatAmount = 0): boolean =>
  (vatRatePercent ?? 0) > 0 || vatAmount > 0;

// Actual price and the price with VAT, for price columns.
export const priceWithVat = (actualPrice: number, percent: number): number =>
  roundToTwoDecimals(actualPrice + calculateVatAmount(actualPrice, percent));
